import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { Box } from '@mui/material'
import React, { useState } from 'react'
import { LuX } from 'react-icons/lu'

import Icon from '@/components/Icon'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import BrandValue from '@/components/konva/components/BrandValue'

const LabelValuePair = ({
  value,
  label,
  column = false,
}: {
  value: number | string
  label: string | React.ReactNode
  column?: boolean
}) => {
  return (
    <Box
      fontSize="14px"
      letterSpacing="0.5px"
      lineHeight="normal"
      gap={1}
      display="flex"
      flexDirection={column ? 'column' : 'row'}
    >
      <Box display="flex" alignItems="center">
        {label}
      </Box>
      <Box
        bgcolor="rgba(33, 33, 33, 0.80)"
        fontSize="12px"
        lineHeight="20px"
        letterSpacing="0.25"
        px="6px"
        py="2px"
        borderRadius="4px"
        width="fit-content"
        whiteSpace="nowrap"
      >
        {value}
      </Box>
    </Box>
  )
}

const OutputCard = ({
  output,
  setTooltipOpen,
}: {
  output: any
  setTooltipOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const colors = ['#68CEA1', '#FFB975']
  const [expanded, setExpanded] = useState(false)

  return (
    <Box
      display="flex"
      flexGrow={1}
      flexDirection="column"
      gap={1}
      alignItems="flex"
    >
      {expanded && (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
            gap={1}
          >
            <LabelWithTooltip
              titleSx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
              tooltipSx={{ height: '16px', width: '16px' }}
              label="Staining Target in LAB"
              tooltipText="Apply the staining recipe on the framework and measure before baking it with Optishade to verify the staining target in LAB values. The Chroma ( a*+b*) should be very close to the target. The Luminosity will be higher before baking it because it will drop after firing it."
            />
            <Icon icon={LuX} size={18} onClick={() => setTooltipOpen(false)} />
            <CloseFullscreenIcon
              onClick={() => setExpanded((prev) => !prev)}
              sx={{ height: 16, width: 16, cursor: 'pointer' }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
          >
            <LabelValuePair label="L:" value={output?.LABafterstaining?.[0]} />
            <LabelValuePair label="a:" value={output?.LABafterstaining?.[1]} />
            <LabelValuePair label="b:" value={output?.LABafterstaining?.[2]} />
          </Box>
        </>
      )}
      {output?.provided_input?.stainingbrand && (
        <>
          {expanded && (
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          )}
          <BrandValue
            value={output.provided_input.stainingbrand || ''}
            expanded={expanded}
            setExpanded={setExpanded}
            setTooltipOpen={setTooltipOpen}
          />
        </>
      )}
      {output?.stainingrecipe?.stainingthickness && (
        <>
          <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          <LabelValuePair
            label="Staining Intensity"
            value={`${output?.stainingrecipe?.stainingthickness?.toFixed(2)}mm`}
          />
        </>
      )}
      {output?.stainingrecipe?.powders?.length && (
        <>
          <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          {output?.stainingrecipe?.powders?.map((p, i) => (
            <LabelValuePair
              key={`powder-${i}`}
              label={
                <Box
                  height="16px"
                  width="16px"
                  borderRadius={50}
                  bgcolor={colors?.[i] || '#68cea1'}
                ></Box>
              }
              value={`${output?.stainingrecipe?.ratios[i]} - ${p}`}
            />
          ))}
        </>
      )}
    </Box>
  )
}

export default OutputCard
